<!-- src/components/PublicSubHeader.vue -->
<template>
  <div class="bg-white relative z-40 border-b border-slate-100">
    <!-- FIX: Width and Padding strictly synchronized with SubHeader.vue protocol -->
    <div class="max-w-7xl mx-auto px-4 sm:px-0 py-4">
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        
        <!-- Left Side: Context & Branding -->
        <div class="flex items-center gap-6">
          <div v-if="logoUrl" class="hidden sm:block">
            <img :src="logoUrl" class="h-12 w-auto object-contain" alt="Branding Logo">
          </div>
          <div v-if="logoUrl" class="h-8 w-px bg-gray-200 hidden sm:block"></div>
          <div>
            <h2 class="text-sm font-black text-[#0A1630] uppercase tracking-[0.2em] flex items-center gap-2">
               {{ title }}
            </h2>
          </div>
        </div>

        <!-- Right Side: Chronological & Rail Metadata -->
        <div class="flex flex-row items-center justify-between w-full md:w-auto md:justify-end gap-4 md:gap-8">
          
          <!-- Timestamp Group -->
          <div class="text-left md:text-right">
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-tighter">{{ currentDateString }}</p>
            <p class="text-xs font-bold text-slate-700 tabular-nums">{{ currentTimeString }}</p>
          </div>
          
          <div class="h-10 w-px bg-gray-100 hidden md:block"></div>

          <!-- Premium Rail Week Counter -->
          <div class="bg-[#0A1630] px-5 py-2.5 rounded-xl shadow-lg border border-slate-800 transition-transform hover:scale-105 flex flex-col items-center justify-center min-w-[100px]">
            <p class="text-[9px] font-black text-[#20FCF1] uppercase tracking-[0.3em] leading-none mb-1.5 text-center">Rail Week</p>
            <p class="text-2xl font-black text-white leading-none tabular-nums text-center w-full">{{ currentRailWeek }}</p>
          </div>
        </div>
        
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

defineProps({
  title: {
    type: String,
    default: 'Sentinel Safe'
  },
  logoUrl: {
    type: String,
    default: ''
  }
});

const currentDateString = ref('');
const currentTimeString = ref('');
const currentRailWeek = ref('00');
let timerId = null;

/**
 * DETERMINISTIC RAIL WEEK CALCULATION
 * Synchronized with core platform logic
 */
const calculateRailWeek = () => {
  const now = new Date();
  
  const getStartSaturday = (year) => {
    let target = new Date(year, 2, 29); // March 29th
    while (target.getDay() !== 6) {
      target.setDate(target.getDate() + 1);
    }
    target.setHours(0, 0, 0, 0);
    return target;
  };

  const currentYear = now.getFullYear();
  let railYearStart = getStartSaturday(currentYear);

  if (now < railYearStart) {
    railYearStart = getStartSaturday(currentYear - 1);
  }

  const diffInMs = now - railYearStart;
  const diffInWeeks = Math.floor(diffInMs / (7 * 24 * 60 * 60 * 1000));
  const weekNum = diffInWeeks + 1;
  
  currentRailWeek.value = weekNum.toString().padStart(2, '0');
};

const updateClock = () => {
  const now = new Date();
  currentTimeString.value = now.toLocaleTimeString('en-GB', { 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit' 
  });
  currentDateString.value = now.toLocaleDateString('en-GB', { 
    weekday: 'long', 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
};

onMounted(() => {
  updateClock();
  calculateRailWeek();
  
  timerId = setInterval(() => {
    updateClock();
    const now = new Date();
    // Refresh Rail Week at midnight
    if (now.getHours() === 0 && now.getMinutes() === 0 && now.getSeconds() === 0) {
      calculateRailWeek();
    }
  }, 1000);
});

onUnmounted(() => {
  if (timerId) {
    clearInterval(timerId); 
  }
});
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { 
  from { opacity: 0; transform: translateY(10px); } 
  to { opacity: 1; transform: translateY(0); } 
}
</style>